import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFetchListData } from "../../hooks/useFetchListData";
import {
  CheckCircleOutlineRoundedIcon,
  CancelRoundedIcon,
  TextSnippetRoundedIcon,
  InfoIcon,
  Box,
  GridRowAction,
  convertToCurrencyFormat,
  DataGridTable,
  PricingPortalAPI,
  PopperMessage,
} from "./index";

const QuoteApprovalDataGrid = (props) => {
  /**************************************************
  //Component handlers & hooks
  **************************************************/
  const { t } = useTranslation();
  const {
    user,
    settingsConfig,
    spinnerCtx,
    listWithTab,
    rejectHandler,
    setRejectHandler,
    approveOnClick,
    coveringManager,
    refreshGridHandler,
    setRefreshGridHandler,
    handleRedirectionQuoteDetails,
  } = props;

  /**************************************************
  //Component state variables
  **************************************************/
  const [quoteApprovalRecordList, setQuoteApprovalRecordList] = useState([]);
  const [dataGridPageSize, setDataGridPageSize] = useState(10);
  const [gridPageSizeOptions] = useState([5, 10, 15, 20]);
  const [popperMessage, setPopperMessage] = useState({
    isOpen: false,
    anchorEl: null,
    message: "",
  });

  const QuoteApprovalList = useFetchListData(
    settingsConfig.businessCode,
    settingsConfig.countryCode,
    "getQuoteApprovalList",
    "quoteApproval",
    { required: true, clause: coveringManager === true ? "&coveringManager=true&quoteId=" : "&coveringManager=false&quoteId=" }
  );

  const dataGridSchema = [
    {
      field: "quoteNumber",
      headerName: t("quoteApproval.quoteNumber"),
      flex: 1,
    },
    {
      field: "colleagueName",
      headerName: t("quoteApproval.surveyor"),
      flex: 2,
    },
    {
      field: "originalPrice",
      headerName: t("quoteApproval.originalPrice"),
      flex: 1,
    },
    {
      field: "discountedPrice",
      headerName: t("quoteApproval.quotationPrice"),
      flex: 1,
    },
    {
      field: "pendingWith",
      headerName: t("quoteApproval.pendingWith"),
      flex: 2,
    },
    {
      field: "requestedApprovalLevel",
      headerName: t("quoteApproval.approvalLevel"),
      flex: 1,
    },
    {
      field: "status",
      headerName: t("quoteApproval.status"),
      flex: 1,
    },
    {
      field: "recordResponse",
      hide: true,
    },
    {
      field: "action",
      headerName: t("quoteApproval.action"),
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            <GridRowAction width={25} height={25} handleSubmit={() => handleRedirectionQuoteDetails(params.row)}>
              <TextSnippetRoundedIcon fontSize="large" style={{ color: "#354A55", fontSize: "2rem" }} />
            </GridRowAction>
            {params.row.additionalMessage && (
              <GridRowAction width={25} height={25} handleSubmit={handleDisplayAdditionalMsg(params.row)}>
                <InfoIcon
                  style={{
                    color: "#354A55",
                    fontSize: "2rem",
                  }}
                  id={params.row.id}
                />
              </GridRowAction>
            )}
            <Box width={25} />
            {((params.row.recordResponse &&
              params.row.recordResponse.approvalAuthorityEmail &&
              params.row.recordResponse.approvalAuthorityEmail.toLowerCase() === user.userRecord.email.toLowerCase()) ||
              coveringManager === true) && (
              <>
                <GridRowAction
                  width={25}
                  height={25}
                  disabled={false}
                  handleSubmit={() => handleApproveRejectClick(params.row, "approve")}
                >
                  <CheckCircleOutlineRoundedIcon fontSize="large" style={{ color: "#354A55" }} />
                </GridRowAction>
                <GridRowAction
                  width={25}
                  height={25}
                  disabled={false}
                  handleSubmit={() => handleApproveRejectClick(params.row, "reject")}
                >
                  <CancelRoundedIcon fontSize="large" style={{ color: "#354A55" }} />
                </GridRowAction>
              </>
            )}
          </>
        );
      },
    },
  ];

  /**************************************************
  //Fetch APIs & logic
  **************************************************/

  const updateQuoteList = (quoteId, record) => {
    try {
      let _updatedQuoteList = [...quoteApprovalRecordList];
      let _index = _updatedQuoteList.findIndex((item) => item.id === quoteId);
      let _isDelete = !record.length > 0;

      if (!_isDelete) {
        let item = record[0];
        let _updatedQuoteObj = {
          id: item.quoteId,
          quoteNumber: item.prospectNumber && item.quoteNumber ? item.prospectNumber + "/" + item.quoteNumber : "",
          colleagueName: item.salesPersonEmailId ? item.salesPersonEmailId : "",
          originalPrice: item.originalPrice && item.locale ? convertToCurrencyFormat(item.locale, item.originalPrice) : 0,
          discountedPrice: item.discountedPrice && item.locale ? convertToCurrencyFormat(item.locale, item.discountedPrice) : 0,
          pendingWith: item.approvalAuthorityEmail ? item.approvalAuthorityEmail : "",
          status: item.status ? t("status." + item.status) : "",
          recordResponse: item,
          requestedApprovalLevel: item.requestedLevel ? item.requestedLevel : "",
          additionalMessage: item.additionalMessage ? item.additionalMessage : null,
        };
        _updatedQuoteList[_index] = _updatedQuoteObj;
      } else {
        _updatedQuoteList.splice(_index, 1);
      }
      setQuoteApprovalRecordList(_updatedQuoteList);
      spinnerCtx.setLoadingIndicator(false);
    } catch (err) {
      spinnerCtx.setLoadingIndicator(false);
      alert(err);
    }
  };

  const postActionRefreshList = async (quoteId) => {
    try {
      let _quoteApprovalRecord = await PricingPortalAPI.getFilterList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getQuoteApprovalList",
        "quoteApproval",
        coveringManager === true ? "&coveringManager=true&quoteId=" + quoteId : "&coveringManager=false&quoteId=" + quoteId
      );
      setRefreshGridHandler((prevState) => ({
        ...prevState,
        isRequired: false,
        quoteId: "",
      }));
      updateQuoteList(quoteId, _quoteApprovalRecord);
    } catch (err) {
      setRefreshGridHandler((prevState) => ({
        ...prevState,
        isRequired: false,
        quoteId: "",
      }));
      spinnerCtx.setLoadingIndicator(false);
      alert(err);
    }
  };

  const setQuoteApprovalList = (quoteList) => {
    try {
      if (quoteList.length) quoteList.sort((a, b) => new Date(b.modifiedDate).getTime() - new Date(a.modifiedDate).getTime());
      let _quoteApprovalList = quoteList.map((item) => {
        return {
          id: item.quoteId,
          quoteNumber: item.prospectNumber && item.quoteNumber ? item.prospectNumber + "/" + item.quoteNumber : "",
          colleagueName: item.salesPersonEmailId ? item.salesPersonEmailId : "",
          originalPrice: item.originalPrice && item.locale ? convertToCurrencyFormat(item.locale, item.originalPrice) : 0,
          discountedPrice: item.discountedPrice && item.locale ? convertToCurrencyFormat(item.locale, item.discountedPrice) : 0,
          pendingWith: item.approvalAuthorityEmail ? item.approvalAuthorityEmail : "",
          status: item.status ? t("status." + item.status) : "",
          recordResponse: item,
          requestedApprovalLevel: item.requestedLevel ? item.requestedLevel : "",
          additionalMessage: item.additionalMessage ? item.additionalMessage : null,
        };
      });

      setQuoteApprovalRecordList(_quoteApprovalList);
      spinnerCtx.setLoadingIndicator(false);
    } catch (err) {
      spinnerCtx.setLoadingIndicator(false);
      alert(err);
    }
  };

  /**************************************************
  //Event definitions
  **************************************************/
  const handleApproveRejectClick = async (param, eventId) => {
    try {
      if (eventId === "approve") {
        await approveOnClick(param?.recordResponse);
      } else if (eventId === "reject" && !rejectHandler.rejectionReason && !rejectHandler.isReject) {
        setRejectHandler((prevState) => ({
          ...prevState,
          isReject: true,
          rejectedQuoteRecord: param?.recordResponse,
        }));
      }
    } catch (err) {
      alert(err);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setDataGridPageSize(newPageSize);
  };

  const handleDisplayAdditionalMsg = (selectedQuote) => (event) => {
    try {
      setPopperMessage({
        isOpen: popperMessage.anchorEl
          ? popperMessage.anchorEl.firstChild.id === selectedQuote.id
            ? !popperMessage.isOpen
            : true
          : true,
        anchorEl: event.currentTarget,
        message: selectedQuote.additionalMessage,
      });
    } catch (e) {
      alert(e);
    }
  };

  /**************************************************
  //Component on load useEffect
  **************************************************/
  useEffect(() => {
    spinnerCtx.setLoadingIndicator(true);
    if (QuoteApprovalList) {
      setQuoteApprovalList(QuoteApprovalList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [QuoteApprovalList]);

  useEffect(() => {
    if (refreshGridHandler.isRequired === true && refreshGridHandler.quoteId) {
      spinnerCtx.setLoadingIndicator(true);
      postActionRefreshList(refreshGridHandler.quoteId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshGridHandler]);

  return (
    <>
      <DataGridTable
        listWithTab={listWithTab}
        paddingTop="0px"
        rows={quoteApprovalRecordList}
        columns={dataGridSchema}
        pageSize={dataGridPageSize}
        pageSizeOptions={gridPageSizeOptions}
        onPageSizeChange={handlePageSizeChange}
        toolbarHeader={coveringManager === true ? "Redirected Quote Approval" : t("quoteApproval.header")}
        toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
      />
      <PopperMessage
        placement={"bottom-end"}
        anchorEl={popperMessage.anchorEl}
        open={popperMessage.isOpen}
        message={popperMessage.message}
      />
    </>
  );
};

export default QuoteApprovalDataGrid;
